/**
 * Cross-panel chart synchronization (zoom + hover crosshair/tooltip).
 *
 * Any time-series panels rendered inside a <DashboardSyncProvider> share one
 * view window and a linked crosshair. Panels register their Highcharts instance
 * on ready; the provider keeps them locked together and re-applies the shared
 * window whenever a panel re-seeds its data.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import type * as Highcharts from "highcharts/highstock";

export interface DashboardSyncApi {
  /** Register a chart; returns an unregister function. */
  register(chart: Highcharts.Chart): () => void;
  /** Propagate a new view window to every registered chart. */
  syncExtremes(min: number | undefined, max: number | undefined): void;
  /** The shared window, or null for full range. Read when re-seeding data. */
  getViewRange(): { min: number; max: number } | null;
  /** True while extremes are being applied (to suppress feedback loops). */
  isSyncing(): boolean;
}

const DashboardSyncContext = createContext<DashboardSyncApi | null>(null);

export const DashboardSyncProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const charts = useRef<Set<Highcharts.Chart>>(new Set());
  const syncing = useRef(false);
  const viewRange = useRef<{ min: number; max: number } | null>(null);

  const syncExtremes = useCallback(
    (min: number | undefined, max: number | undefined) => {
      // Ignore re-entrant calls from the cascade below: when we apply extremes
      // to the other charts, their afterSetExtremes fires and calls back here.
      // Those must NOT overwrite the shared window (a chart whose data is
      // narrower than the zoom would otherwise clear it to full range).
      if (syncing.current) return;
      viewRange.current =
        typeof min === "number" && typeof max === "number"
          ? { min, max }
          : null;
      syncing.current = true;
      try {
        charts.current.forEach((chart) => {
          const axis = chart.xAxis?.[0];
          if (!axis) return;
          const ext = axis.getExtremes();
          if (ext.min !== min || ext.max !== max) {
            try {
              axis.setExtremes(min, max, true, false);
            } catch {
              /* chart destroyed */
            }
          }
        });
      } finally {
        syncing.current = false;
      }
    },
    [],
  );

  const register = useCallback((chart: Highcharts.Chart) => {
    charts.current.add(chart);

    const el = chart.container;
    const move = (e: Event) => {
      charts.current.forEach((other) => {
        try {
          const evt = (other as any).pointer?.normalize(e);
          const pt = other.series?.[0]?.searchPoint(evt, true);
          if (pt) pt.onMouseOver();
        } catch {
          /* destroyed / no data */
        }
      });
    };
    const leave = () => {
      charts.current.forEach((other) => {
        try {
          other.tooltip?.hide();
          other.xAxis?.[0]?.hideCrosshair();
          const s = other.series?.[0] as any;
          if (s?.setState) s.setState("");
        } catch {
          /* destroyed */
        }
      });
    };
    el?.addEventListener("mousemove", move);
    el?.addEventListener("touchmove", move);
    el?.addEventListener("mouseleave", leave);

    return () => {
      charts.current.delete(chart);
      el?.removeEventListener("mousemove", move);
      el?.removeEventListener("touchmove", move);
      el?.removeEventListener("mouseleave", leave);
    };
  }, []);

  const api = useMemo<DashboardSyncApi>(
    () => ({
      register,
      syncExtremes,
      getViewRange: () => viewRange.current,
      isSyncing: () => syncing.current,
    }),
    [register, syncExtremes],
  );

  return (
    <DashboardSyncContext.Provider value={api}>
      {children}
    </DashboardSyncContext.Provider>
  );
};

/** Access the sync API, or null when a panel is rendered outside a provider. */
export function useDashboardSync(): DashboardSyncApi | null {
  return useContext(DashboardSyncContext);
}
